// src/features/clients/hooks/useClients.js
import { useState, useCallback } from "react";
import { db } from "services/firebaseConfig";
import {
  collection,
  query,
  getDocs,
  doc,
  setDoc,
  deleteDoc,
  Timestamp,
  orderBy,
} from "firebase/firestore";
import toast from "react-hot-toast";

/**
 * Normaliza o telefone para garantir casamento consistente entre coleções
 * mesmo se gravado com 8, 9, 10 ou 11 dígitos, ou com código de país +55.
 */
export const normalizePhone = (p) => {
  if (!p) return "";
  let digits = String(p).replace(/\D/g, "");
  if (digits.startsWith("55") && digits.length > 11) {
    digits = digits.substring(2);
  }
  return digits.length >= 8 ? digits.slice(-8) : digits;
};

/**
 * Calcula a distância em dias e gera um rótulo amigável em português.
 */
export const calcularRecencia = (dataObj) => {
  if (!dataObj) return { dias: 999999, rotulo: "Sem registro" };
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  const data = new Date(dataObj);
  data.setHours(0, 0, 0, 0);

  const diffTime = hoje - data;
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays < 0) {
    const futuros = Math.abs(diffDays);
    if (futuros === 1) return { dias: diffDays, rotulo: "Amanhã" };
    return { dias: diffDays, rotulo: `Em ${futuros} dias (Futura)` };
  }
  if (diffDays === 0) return { dias: 0, rotulo: "Hoje" };
  if (diffDays === 1) return { dias: 1, rotulo: "Ontem" };
  if (diffDays < 7) return { dias: diffDays, rotulo: `Há ${diffDays} dias` };
  if (diffDays < 30) {
    const semanas = Math.floor(diffDays / 7);
    return { dias: diffDays, rotulo: `Há ${semanas} semana${semanas > 1 ? "s" : ""}` };
  }
  if (diffDays < 365) {
    const meses = Math.floor(diffDays / 30);
    return { dias: diffDays, rotulo: `Há ${meses} m${meses > 1 ? "eses" : "ês"}` };
  }
  const anos = Math.floor(diffDays / 365);
  return { dias: diffDays, rotulo: `Há mais de ${anos} ano${anos > 1 ? "s" : ""}` };
};

export const useClients = () => {
  const [clients, setClients] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Busca todos os clientes e cruza em tempo real com Chalés e Excursões
  const fetchClients = useCallback(async () => {
    setIsLoading(true);
    try {
      // Busca em paralelo para alta performance
      const [clientsSnap, reservationsSnap, excursionsSnap] = await Promise.all([
        getDocs(query(collection(db, "clients"), orderBy("name"))),
        getDocs(collection(db, "reservations")),
        getDocs(collection(db, "excursions")),
      ]);

      // 1. Indexa reservas de chalé por telefone normalizado
      const resByPhone = {};
      reservationsSnap.forEach((docSnap) => {
        const data = docSnap.data();
        const key = normalizePhone(data.phoneInfo);
        if (!key) return;
        if (!resByPhone[key]) resByPhone[key] = [];
        resByPhone[key].push({ id: docSnap.id, ...data });
      });

      // 2. Indexa excursões por telefone normalizado
      const excByPhone = {};
      excursionsSnap.forEach((docSnap) => {
        const data = docSnap.data();
        const key = normalizePhone(data.clientPhone);
        if (!key) return;
        if (!excByPhone[key]) excByPhone[key] = [];
        excByPhone[key].push({ id: docSnap.id, ...data });
      });

      // 3. Enriquece os clientes com os dados consolidados
      const fetchedClients = [];
      clientsSnap.forEach((docSnap) => {
        const clientData = docSnap.data();
        const key = normalizePhone(clientData.phone || docSnap.id);

        const chales = (resByPhone[key] || []).sort((a, b) => {
          const dateA = a.checkInDate?.toDate ? a.checkInDate.toDate() : new Date(a.checkInDate || 0);
          const dateB = b.checkInDate?.toDate ? b.checkInDate.toDate() : new Date(b.checkInDate || 0);
          return dateB - dateA;
        });

        const excursions = (excByPhone[key] || []).sort((a, b) => {
          const dateA = a.excursionDate?.toDate ? a.excursionDate.toDate() : new Date(a.excursionDate || 0);
          const dateB = b.excursionDate?.toDate ? b.excursionDate.toDate() : new Date(b.excursionDate || 0);
          return dateB - dateA;
        });

        const lastChale = chales[0];
        const lastExc = excursions[0];

        const dChale = lastChale
          ? lastChale.checkInDate?.toDate
            ? lastChale.checkInDate.toDate()
            : new Date(lastChale.checkInDate || 0)
          : null;
        const dExc = lastExc
          ? lastExc.excursionDate?.toDate
            ? lastExc.excursionDate.toDate()
            : new Date(lastExc.excursionDate || 0)
          : null;

        let ultimaReservaDate = null;
        let ultimaModalidade = null;
        let detalheUltima = "";

        if (dChale && dExc) {
          if (dChale >= dExc) {
            ultimaReservaDate = dChale;
            ultimaModalidade = "chale";
            detalheUltima = `Chalé ${lastChale.chaletNumber || ""}`;
          } else {
            ultimaReservaDate = dExc;
            ultimaModalidade = "excursao";
            detalheUltima = `Excursão ${lastExc.city ? `(${lastExc.city})` : ""}`;
          }
        } else if (dChale) {
          ultimaReservaDate = dChale;
          ultimaModalidade = "chale";
          detalheUltima = `Chalé ${lastChale.chaletNumber || ""}`;
        } else if (dExc) {
          ultimaReservaDate = dExc;
          ultimaModalidade = "excursao";
          detalheUltima = `Excursão ${lastExc.city ? `(${lastExc.city})` : ""}`;
        }

        let modalidade = "nenhuma";
        if (chales.length > 0 && excursions.length > 0) modalidade = "ambos";
        else if (chales.length > 0) modalidade = "chale";
        else if (excursions.length > 0) modalidade = "excursao";

        const recencia = calcularRecencia(ultimaReservaDate);

        fetchedClients.push({
          id: docSnap.id,
          ...clientData,
          modalidade,
          chales,
          excursions,
          totalChales: chales.length,
          totalExcursoes: excursions.length,
          totalReservas: chales.length + excursions.length,
          ultimaReservaDate,
          ultimaReservaFormatada: ultimaReservaDate ? ultimaReservaDate.toLocaleDateString("pt-BR") : "",
          diasDesdeUltima: recencia.dias,
          rotuloRecencia: recencia.rotulo,
          ultimaModalidade,
          detalheUltima,
        });
      });

      setClients(fetchedClients);
    } catch (error) {
      console.error("Erro ao buscar clientes:", error);
      toast.error("Erro ao carregar clientes.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Adiciona ou Atualiza um cliente
  const addClient = async (data) => {
    try {
      const cleanPhone = data.phone.replace(/\D/g, "");

      if (!cleanPhone) {
        throw new Error("Telefone inválido para cadastro.");
      }

      const docId = cleanPhone;
      const docRef = doc(db, "clients", docId);

      const clientData = {
        name: data.name,
        phone: data.phone,
        cpf: data.cpf || "",
        city: data.city || "",
        createdAt: data.createdAt || Timestamp.now(),
        lastUpdate: Timestamp.now(),
        source: data.source || "manual",
      };

      await setDoc(docRef, clientData, { merge: true });
      return true;
    } catch (error) {
      console.error("Erro ao salvar cliente:", error);
      toast.error(`Erro ao salvar cliente: ${error.message}`);
      return false;
    }
  };

  const deleteClient = async (clientId) => {
    if (window.confirm("Tem certeza que deseja excluir este cliente?")) {
      try {
        await deleteDoc(doc(db, "clients", clientId));
        toast.success("Cliente excluído.");
        fetchClients();
      } catch (error) {
        toast.error("Erro ao excluir cliente.");
      }
    }
  };

  return {
    clients,
    isLoading,
    fetchClients,
    addClient,
    deleteClient,
  };
};
